import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./ExpWithHumansPage.css";
import { ArrowLeft, Check } from "lucide-react";
import progressIcon from "../../assets/progressIcon6.png";
import nextIcon from "../../assets/nextButtonIcon.png";
import dogLevel0 from "../../assets/dogLevel0.png";
import dogLevel1 from "../../assets/dogLevel1.png";
import dogLevel2 from "../../assets/dogLevel2.png";
import dogLevel3 from "../../assets/dogLevel3.png";
import dogLevel4 from "../../assets/dogLevel4.png";
import appIcon from "../../assets/appIcon.svg";
import MobileTopBar from "../../components/MobileTopBar";
import MobileBottomBar from "../../components/MobileBottomBar";
import DesktopTopBar from "../../components/DesktopTopBar";
import DesktopBottomBar from "../../components/DesktopBottomBar";

const ExpWithHumansPage = ({ formData, setFormData }) => {
  const navigate = useNavigate();
  const sliderRef = useRef(null);
  const isDraggingRef = useRef(false);

  const dogName = formData.dogName || "{Dog Name}";

  const TRACK_START = 5;
  const TRACK_END = 95;
  const TRACK_RANGE = TRACK_END - TRACK_START;
  const SLIDER_STEPS = 4;

  const experienceLevels = [
    { value: 0, label: "not friendly with any other humans" },
    { value: 1, label: "not friendly with most humans" },
    { value: 2, label: "50/50 friendly with humans" },
    { value: 3, label: "friendly with most humans" },
    { value: 4, label: "friendly with all humans" },
  ];

  const getInitialValue = () => {
    if (formData.socialExperienceWithHumans?.value !== undefined) {
      return formData.socialExperienceWithHumans.value;
    }
    const strVal =
      formData.alongWithOtherHumans || formData.along_with_other_humans;
    if (typeof strVal === "string") {
      const foundIdx = experienceLevels.findIndex(
        (lvl) => lvl.label.toLowerCase() === strVal.toLowerCase(),
      );
      if (foundIdx !== -1) return foundIdx;
    }
    return 1;
  };

  const [sliderValue, setSliderValue] = useState(getInitialValue);

  useEffect(() => {
    setSliderValue(getInitialValue());
  }, [formData]);

  const handleSliderChange = (value) => {
    const clamped = Math.max(0, Math.min(SLIDER_STEPS, value));
    setSliderValue(clamped);
  };

  const handleNext = () => {
    const selectedObj = experienceLevels[sliderValue];
    setFormData((prev) => ({
      ...prev,
      socialExperienceWithHumans: {
        value: sliderValue,
        label: selectedObj.label,
      },
      alongWithOtherHumans: selectedObj.label,
      along_with_other_humans: selectedObj.label,
    }));

    navigate("/write-dog-bio");
  };

  const handleSkip = () => {
    navigate("/write-dog-bio");
  };

  const dogLevelImages = [
    dogLevel0,
    dogLevel1,
    dogLevel2,
    dogLevel3,
    dogLevel4,
  ];

  const currentDogLevelImage = dogLevelImages[sliderValue] || dogLevel0;

  const updateValueFromPosition = (clientX, clientY) => {
    if (!sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const isMobile = window.innerWidth <= 1023;

    let percent;
    if (isMobile) {
      let y = clientY - rect.top;
      y = Math.max(0, Math.min(rect.height, y));
      percent = y / rect.height;
    } else {
      let x = clientX - rect.left;
      x = Math.max(0, Math.min(rect.width, x));
      percent = x / rect.width;
    }

    const steppedValue = Math.round(percent * SLIDER_STEPS);
    handleSliderChange(steppedValue);
  };

  const handlePointerDown = (e) => {
    e.stopPropagation();
    isDraggingRef.current = true;
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;
    if (clientX !== undefined && clientY !== undefined) {
      updateValueFromPosition(clientX, clientY);
    }
  };

  const handlePointerMove = (e) => {
    if (!isDraggingRef.current) return;
    if (e.cancelable) {
      e.preventDefault();
    }
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;
    updateValueFromPosition(clientX, clientY);
  };

  const stopDragging = () => {
    isDraggingRef.current = false;
  };

  const handleTrackClick = (e) => {
    const clientX = e.clientX;
    const clientY = e.clientY;
    if (clientX !== undefined && clientY !== undefined) {
      updateValueFromPosition(clientX, clientY);
    }
  };

  useEffect(() => {
    window.addEventListener("mousemove", handlePointerMove);
    window.addEventListener("mouseup", stopDragging);
    window.addEventListener("touchmove", handlePointerMove, { passive: false });
    window.addEventListener("touchend", stopDragging);

    return () => {
      window.removeEventListener("mousemove", handlePointerMove);
      window.removeEventListener("mouseup", stopDragging);
      window.removeEventListener("touchmove", handlePointerMove);
      window.removeEventListener("touchend", stopDragging);
    };
  }, []);

  const valPosPercent = `${TRACK_START + (sliderValue / SLIDER_STEPS) * TRACK_RANGE}%`;

  return (
    <div className="h-exp-container">
      <MobileTopBar
        onBack={() => navigate("/exp-with-dogs")}
        backColor="#fff"
        logoColor="#fff"
        isProtectedRoute={false}
      />

      <DesktopTopBar
        showBack={true}
        onBack={() => navigate("/exp-with-dogs")}
        backColor="#fff"
        showProgress={true}
        progressIconSrc={progressIcon}
        progressPercent={100}
        iconLeft={95}
        iconWidth="80px"
      />

      <div className="h-exp-content">
        <div className="h-mobile-progress-wrapper h-mobile-only">
          <div className="h-dog-icon-top">
            <img
              src={progressIcon}
              alt="Progress"
              className="h-progress-icon-img"
            />
          </div>
          <div
            className="h-progress-bar"
            style={{
              borderWidth: "1px",
              borderStyle: "solid",
              borderColor: "white",
            }}
          >
            <div className="h-progress-fill" style={{ width: "100%" }}></div>
          </div>
        </div>

        <h1 className="h-exp-title">
          HOW DOES {dogName.toUpperCase()} GET ALONG WITH OTHER HUMANS?
        </h1>
        <p className="h-exp-subtitle">Honesty is the best Pawlicy!</p>

        <div
          className="h-slider-container"
          ref={sliderRef}
          onClick={handleTrackClick}
          style={{ "--pos": valPosPercent }}
        >
          <div className="h-slider-track-bg"></div>
          <div className="h-slider-track-fill"></div>

          <div className="h-slider-steps-wrapper">
            {[...Array(SLIDER_STEPS + 1)].map((_, index) => {
              const stepPosPercent = `${TRACK_START + (index / SLIDER_STEPS) * TRACK_RANGE}%`;
              return (
                <div
                  key={index}
                  className={`h-slider-step-item ${index % 2 === 0 ? "left-label" : "right-label"}`}
                  style={{ "--step-pos": stepPosPercent }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSliderChange(index);
                  }}
                >
                  <div className={`h-slider-dot`}></div>
                  <span
                    className={`h-slider-label ${
                      index === sliderValue ? "h-active-label" : ""
                    }`}
                  >
                    {experienceLevels[index]?.label}
                  </span>
                </div>
              );
            })}
          </div>

          <div
            className="h-slider-thumb-dog"
            onMouseDown={handlePointerDown}
            onTouchStart={handlePointerDown}
            style={{
              left:
                window.innerWidth <= 1023
                  ? sliderValue <= 2
                    ? "50%"
                    : "49%"
                  : undefined,
            }}
          >
            <img
              src={currentDogLevelImage}
              alt={`DogIcon`}
              draggable={false}
              onDragStart={(e) => e.preventDefault()}
              className={`h-dog-level-icon ${
                sliderValue === 3 || sliderValue === 4
                  ? "h-dog-level-large"
                  : ""
              }`}
            />
          </div>
        </div>
      </div>

      <DesktopBottomBar
        onNext={handleNext}
        isNextEnabled={true}
        nextIconSrc={nextIcon}
        showSkip={true}
        onSkip={handleSkip}
        variant="green"
      />

      <div className="h-exp-footer mobile-only">
        <MobileBottomBar
          onNext={handleNext}
          isNextEnabled={true}
          nextIconSrc={nextIcon}
          showSkip={true}
          onSkip={handleSkip}
          variant="green"
        />
      </div>
    </div>
  );
};

export default ExpWithHumansPage;
